import { Card } from "@/components/containers/Card";
import SectionTitle from "@/components/misc/SectionTitle";
import { Text } from "@/components/text/Text";
import { useStyle } from "@/context/StyleContext";
import { useTimmaContext } from "@/context/TimmaContext";
import React from "react";

import { ArrowDownward, Star } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import HorizontalScrollList from "@/components/containers/HorizontalScrollList";
import {
  SupportedLanguages,
  useLanguageContext,
} from "@/context/LanguageContext";

const testimonials = {
  en: [
    {
      stars: 5,
      by: "Miira",
      description:
        "We went to cut a very curly and big hair. Juanita managed to control and cut hair that no one managed in Helsinki! Turned out AMAZING and a lovely customer service.",
    },
    {
      stars: 5,
      by: "Kaneez",
      description:
        "I had an exceptional experience at this salon. The stylist did an impeccable job with my hair, demonstrating both skill and expertise. She offered valuable suggestions, making it clear that I could trust her with my hair. I highly recommend this salon for anyone looking for a professional and trustworthy hairstylist.",
    },
    {
      stars: 5,
      by: "Chi",
      description:
        "I love my layer cut!!! Service was nice and friendly. I left the salon with beautiful soft hair. My go-to salon from now on ❤️❤️❤️",
    },
  ],
  fi: [
    {
      stars: 5,
      by: "Noora H.",
      description:
        "Huippu kampaaja, eiköhän tästä tullut luottopaikka. 😊 Juanitalla on silmää tehdä luonnollinen, kantajalleen sopiva lopputulos. Annoin vapaat kädet hiusten päivittämiseen vaaleammaksi ja pidän todella paljon lopputuloksesta. Paikassa on todella kiva käydä, Juanita on hurmaava ja huomioi vieraansa todella hyvin. Hän tekee huolellista jälkeä ja välittää asiakkaistaan. Olen käynyt aiemmin laittamassa kulmatkin ja niihin olin myös oikein tyytyväinen. Kannattaa tutustua!",
    },
    {
      stars: 5,
      by: "Sami H.",
      description:
        "Antaisin 10 tähteä jos voisi :-) Eka visiitti tähän parturi-kampaamoon ja olipa todella miellyttävä yllätys. Hiukset ajeltiin hienosti ja tarkasti haluttuun siilimittaan. Ja on pakko kyllä sanoa, että 20 vuoteen en ole saanut yhtä mahtavaa, tarkkaa ja miellyttävää amerikkalaista parranajoa missään, kaikkine pikku nyansseineen. En viitsi paljastaa kaikkea, kannattaa käydä kokeilemassa. Mutta haukkuu todellakin hintansa, ei siis kannata maksaa keskustan hipsteripartureiden ryöstöhintoja parranajosta.",
    },
    {
      stars: 5,
      by: "Jonna S.",
      description:
        "Ripsien ja kulmien värjäys ja siistiminen. Työ tehtiin todella huolellisesti ja otettiin huomioon omat toiveet. Erinomainen lopputulos.",
    },
  ],
  es: [
    {
      stars: 5,
      by: "Marcela D.",
      description:
        "No podría estar más contenta con el corte de pelo que recibí. 🙂‍↔️💗 Hace un mes visité High Hair Room y desde el momento en que entré, me sentí bienvenida y en buenas manos. Tengo el pelo rizado, así que encontrar un buen lugar para cortármelo siempre me ha dado un poco de terror 👉🏼👈🏼. No todos saben cómo manejar rizos, y no quería arriesgarme a ir a cualquier otro salón. Como le comenté a Juanita, este ha sido el mejor corte de pelo que he tenido en mucho tiempo. Respetaron como uso normalmente mi cabello, me escucharon y sin duda volveré.",
    },
    {
      stars: 5,
      by: "Vanessa R.",
      description:
        "Maravillosa, experiencia  100% recomendado! Profesionales y súper amables. Te divertiras y pasaras un buen rato con las estilistas además de salir preciosa!",
    },
    {
      stars: 5,
      by: "Katherine S.",
      description:
        "Me gustó mucho el servicio, creo que cuesta encontrar un lugar así en Finlandia, la atención muy profesional, Sophie me hizo la keratina y el resultado quedó espectacular",
    },
  ],
};

const OurStyle: React.FC = () => {
  const { theme, colors } = useStyle();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const { lang } = useLanguageContext();

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingTop: "20px",
        paddingBottom: "20px",
      }}
    >
      <SectionTitle title={lang.t("landing.ourStyle.title")}>
        <Text style={{ textAlign: "center" }}>
          {lang.t("landing.ourStyle.descriptionLine1")}
        </Text>
        <Text style={{ textAlign: "center" }}>
          {lang.t("landing.ourStyle.descriptionLine2")}
        </Text>
      </SectionTitle>
      {isMobile ? (
        <HorizontalScrollList
          hideScrollBar
          columnGap={"20px"}
          paginate
          navigationSettings={{
            show: true,
            panel: {
              position: "inside",
              visibility: "visible",
            },
          }}
          fullScreenItems
          items={testimonials[lang.locale as SupportedLanguages]}
          renderItem={(testimonial, index) => (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Card
                style={{ maxWidth: "300px" }}
                contentContainerStyle={{
                  rowGap: "16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {new Array(testimonial.stars).fill(0).map((_, starIndex) => (
                    <Star
                      key={`t-star-${index}-${starIndex}`}
                      sx={{ color: "yellow" }}
                    />
                  ))}
                </div>
                <Text type="h3">{testimonial.by}</Text>
                <Text style={{ margin: 0, fontStyle: "italic" }}>
                  "{testimonial.description}"
                </Text>
              </Card>
            </div>
          )}
          keyExtractor={(_, index) => `testimonial-${index}`}
        />
      ) : (
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "20px",
            justifyContent: "space-evenly",
          }}
        >
          {testimonials[lang.locale as SupportedLanguages].map(
            (testimonial, index) => (
              <Card
                key={`testimonial-${index}`}
                style={{ maxWidth: "300px" }}
                contentContainerStyle={{
                  rowGap: "16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {new Array(testimonial.stars).fill(0).map((_, starIndex) => (
                    <Star
                      key={`t-star-${index}-${starIndex}`}
                      sx={{ color: "yellow" }}
                    />
                  ))}
                </div>
                <Text type="h3">{testimonial.by}</Text>
                <Text style={{ margin: 0, fontStyle: "italic" }}>
                  "{testimonial.description}"
                </Text>
              </Card>
            ),
          )}
        </div>
      )}

      <Text
        type="h1"
        containerStyle={{
          padding: "30px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        style={{
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({
            top: document.getElementById("section-about-us")?.offsetTop,
            behavior: "smooth",
          });
        }}
      >
        <ArrowDownward fontSize="inherit" />
      </Text>
    </div>
  );
};

export default OurStyle;
